import { useEffect, useState } from 'react';
import {
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Button,
  Typography,
} from '@mui/material';
import { useUpdateBatchMutation } from '../../api/gridApi';
import {
  useGetInvoiceCompanyBankOptionsMutation,
  type CompanyBankAccountOption,
} from '../../api/companyBankAccountApi';
import { extractErrorMessage } from '../../../../utils/errorUtils';

type CompanyBankAccountDialogProps = {
  open: boolean;
  title?: string | null;
  nodeKey: string;
  invoiceIds: number[];
  targetFieldName: string;
  onClose: () => void;
  onSuccess: (message: string) => void;
};

const getOptionLabel = (option: CompanyBankAccountOption) =>
  option.isCurrent ? `${option.displayName} (current)` : option.displayName;

export const CompanyBankAccountDialog = ({
  open,
  title,
  nodeKey,
  invoiceIds,
  targetFieldName,
  onClose,
  onSuccess,
}: CompanyBankAccountDialogProps) => {
  const [selectedFinanceId, setSelectedFinanceId] = useState('');
  const [validationMessage, setValidationMessage] = useState<string | null>(null);
  const [loadOptions, optionsState] = useGetInvoiceCompanyBankOptionsMutation();
  const [updateBatch, updateState] = useUpdateBatchMutation();

  useEffect(() => {
    if (!open || invoiceIds.length === 0) return;

    void loadOptions({ invoiceIds })
      .unwrap()
      .catch(() => undefined);
  }, [open, invoiceIds, loadOptions]);

  const options = optionsState.data;
  const optionsError = optionsState.error;
  const changeError = updateState.error;
  const errorMessage = optionsError
    ? extractErrorMessage(optionsError, 'Failed to load bank account options.')
    : changeError
      ? extractErrorMessage(changeError, 'Failed to update bank account details.')
      : validationMessage;
  const handleApply = async () => {
    if (!selectedFinanceId) {
      setValidationMessage(
        options?.hasMixedCurrentAccounts
          ? 'Selected invoices use different bank accounts. Please choose one account to apply to all.'
          : 'Please select a bank account.'
      );
      return;
    }

    setValidationMessage(null);

    try {
      await updateBatch({
        nodeKey,
        keys: invoiceIds.map(String),
        fields: [
          {
            FieldName: targetFieldName,
            StringValue: selectedFinanceId,
          },
        ],
      }).unwrap();
      onSuccess(`Bank account details updated for ${invoiceIds.length} invoice(s).`);
    } catch {
      // Error is exposed via updateState.error and rendered in the dialog.
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{title || 'Change Bank account details of Company'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {optionsState.isLoading && (
            <Stack alignItems="center" spacing={1} py={2}>
              <CircularProgress size={24} />
              <Typography variant="body2">Loading bank accounts...</Typography>
            </Stack>
          )}

          {updateState.isLoading && (
            <Typography variant="body2">Updating bank account details...</Typography>
          )}

          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          {options && (
            <>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
                {[
                  `Selected invoices: ${options.invoiceCount ?? invoiceIds.length}`,
                  options.companyName ? `Company: ${options.companyName}` : null,
                  options.currencyName ? `Currency: ${options.currencyName}` : null,
                ]
                  .filter(Boolean)
                  .join('\n')}
              </Typography>

              {options.hasMixedCurrentAccounts && (
                <Alert severity="info">
                  Current accounts differ between selected invoices. Choose a new account below.
                </Alert>
              )}

              {!options.hasMixedCurrentAccounts && options.currentAccountLabel && (
                <Typography variant="body2" color="text.secondary">
                  Current account: {options.currentAccountLabel}
                </Typography>
              )}

              <FormControl fullWidth disabled={optionsState.isLoading || updateState.isLoading}>
                <InputLabel id="company-bank-account-label">Bank account</InputLabel>
                <Select
                  labelId="company-bank-account-label"
                  value={selectedFinanceId}
                  label="Bank account"
                  onChange={(event) => {
                    setValidationMessage(null);
                    setSelectedFinanceId(event.target.value);
                  }}
                >
                  {options.bankAccounts.map((account) => (
                    <MenuItem
                      key={account.crmRelationFinanceId}
                      value={account.crmRelationFinanceId}
                      disabled={account.isCurrent}
                    >
                      {getOptionLabel(account)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updateState.isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleApply}
          disabled={
            !options ||
            optionsState.isLoading ||
            updateState.isLoading ||
            options.bankAccounts.length === 0
          }
        >
          {updateState.isLoading ? <CircularProgress size={20} /> : 'Apply'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyBankAccountDialog;
